import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import React, { useEffect, useContext, useRef, useState } from 'react';
import logo from '../Common/logo.png';

import AuthContext from '../Common/AuthContext';
import { AuthContextValue } from '../Common/types';
import { FileUpload, FileUploadHandle } from './FileUpload';
import { RegisterRequestMinimalInterface } from './types';
import { getOngoingRegisterRequest, updateRegisterRequest } from '../RegisterRequests/registerRequestApi';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import exampleRegisterUpload from '../Common/correct-upload.png';
import { ConfirmRegisterTypeSwitchDialog } from './components/ConfirmRegisterTypeSwitchDialog';
type RegisterType = 'digital' | 'paper';

export default function RegisterUpload() {
  const { profile, logout } = useContext(AuthContext) as AuthContextValue;
  const [open, setOpen] = useState<boolean>(false);
  const [registerType, setRegisterType] = useState<RegisterType | ''>('');
  const [ongoingRegisterRequest, setOngoingRegisterRequest] = useState<RegisterRequestMinimalInterface>();
  const [digitalUploadComplete, setDigitalUploadComplete] = useState(false);
  const [criminalRecordUploadComplete, setCriminalRecordUploadComplete] = useState(false);
  const [suspicionRecordUploadComplete, setSuspicionRecordUploadComplete] = useState(false);
  const [pendingRegisterType, setPendingRegisterType] = useState<RegisterType | null>(null);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);
  const [switchingRegisterType, setSwitchingRegisterType] = useState(false);
  const [exampleImageOpen, setExampleImageOpen] = useState(false);

  const digitalUploadRef = useRef<FileUploadHandle | null>(null);
  const criminalRecordUploadRef = useRef<FileUploadHandle | null>(null);
  const suspicionRecordUploadRef = useRef<FileUploadHandle | null>(null);

  const savedRegisterType =
    ongoingRegisterRequest?.register_type === 'paper' || ongoingRegisterRequest?.register_type === 'digital'
      ? ongoingRegisterRequest.register_type
      : 'digital';

  const effectiveRegisterType: RegisterType | '' = ongoingRegisterRequest?.complete ? savedRegisterType : registerType;

  const canContinue =
    !ongoingRegisterRequest?.complete &&
    (effectiveRegisterType === 'digital'
      ? digitalUploadComplete
      : effectiveRegisterType === 'paper'
      ? criminalRecordUploadComplete && suspicionRecordUploadComplete
      : false);

  useEffect(() => {
    getOngoingRegisterRequest().then(({ data }) => {
      if (data) {
        setOngoingRegisterRequest(data.data);
      }
    });
  }, []);

  function hasUploadedFilesForCurrentType() {
    if (registerType === 'digital') {
      return digitalUploadRef.current?.hasUploadedFiles() ?? false;
    }

    if (registerType === 'paper') {
      return (
        (criminalRecordUploadRef.current?.hasUploadedFiles() ?? false) ||
        (suspicionRecordUploadRef.current?.hasUploadedFiles() ?? false)
      );
    }

    return false;
  }

  function handleRegisterTypeChange(nextRegisterType: RegisterType) {
    if (!registerType) {
      setRegisterType(nextRegisterType);
      return;
    }

    if (nextRegisterType === registerType) return;

    if (hasUploadedFilesForCurrentType()) {
      setPendingRegisterType(nextRegisterType);
      setConfirmSwitchOpen(true);
      return;
    }

    setRegisterType(nextRegisterType);
  }

  async function confirmRegisterTypeSwitch() {
    if (!pendingRegisterType) return;

    setSwitchingRegisterType(true);

    try {
      if (registerType === 'digital') {
        await digitalUploadRef.current?.deleteAllUploadedFiles();
        setDigitalUploadComplete(false);
      }

      if (registerType === 'paper') {
        await Promise.all([
          criminalRecordUploadRef.current?.deleteAllUploadedFiles(),
          suspicionRecordUploadRef.current?.deleteAllUploadedFiles(),
        ]);

        setCriminalRecordUploadComplete(false);
        setSuspicionRecordUploadComplete(false);
      }

      setRegisterType(pendingRegisterType);
      setPendingRegisterType(null);
      setConfirmSwitchOpen(false);
    } finally {
      setSwitchingRegisterType(false);
    }
  }

  function cancelRegisterTypeSwitch() {
    setPendingRegisterType(null);
    setConfirmSwitchOpen(false);
  }

  function handleContinue() {
    if (!ongoingRegisterRequest || !registerType) return;

    updateRegisterRequest(ongoingRegisterRequest.id, {
      complete: true,
      register_type: registerType,
    }).then(() => {
      window.location.reload();
    });
  }

  return (
    <main className="bg-subtleGreen min-h-dvh w-full pt-8 px-8 pb-10">
      <div className="relative w-full mb-10">
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={logo} alt="Logo" width="200px" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="relative w-full mb-3">
          <div className="flex items-center justify-start">
            <div className="text-lg sm:text-xl text-[#5E5E5E]">
              Hej, <span className="font-bold">{profile.first_name}</span> {profile?.surname}
            </div>
          </div>
        </div>

        <div className="mb-10 bg-white shadow-xl rounded-xl p-8 text-[#5E5E5E]">
          {ongoingRegisterRequest ? (
            <>
              {!ongoingRegisterRequest.complete && (
                <div className="border-b border-gray-200 pb-6 mb-8">
                  <p className="text-md sm:text-2xl text-center mb-6">
                    <span className="font-bold">Hur har du fått ditt registerutdrag?</span>
                  </p>

                  <FormControl fullWidth>
                    <RadioGroup
                      value={registerType}
                      onChange={(event) => handleRegisterTypeChange(event.target.value as RegisterType)}
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel
                        value="digital"
                        control={<Radio sx={{ pt: 0.25 }} />}
                        sx={{ alignItems: 'flex-start' }}
                        label={
                          <span className="text-sm sm:text-lg">
                            Jag har fått ett digitalt registerutdrag (t.ex. Kivra, Billo, Min myndighetspost).
                          </span>
                        }
                      />

                      <FormControlLabel
                        value="paper"
                        control={<Radio sx={{ pt: 0.25 }} />}
                        sx={{ alignItems: 'flex-start' }}
                        label={
                          <span className="text-sm sm:text-lg">
                            Jag har fått ett registerutdrag i pappersform via posten.
                          </span>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  <p className="mt-6 text-sm sm:text-lg">
                    OBS. En skärmavbild från polisens hemsida är inte giltigt. Du behöver invänta att ditt utdrag kommer
                    på posten och fota det.
                  </p>
                </div>
              )}

              <div>
                {!effectiveRegisterType ? (
                  <p className="text-[#8B8B8B] text-center text-sm sm:text-lg">
                    Välj ett alternativ ovan för att fortsätta.
                  </p>
                ) : effectiveRegisterType === 'digital' ? (
                  <>
                    <p className="text-md sm:text-2xl text-center mb-6">
                      <span className="font-bold">
                        Digitalt registerutdrag {ongoingRegisterRequest.complete && 'uppladdat!'}
                      </span>
                      {!ongoingRegisterRequest.complete && '(uppladdning)'}
                    </p>

                    <div className="text-sm sm:text-lg">
                      {ongoingRegisterRequest.complete ? (
                        <>
                          <p className="mb-4">
                            Vi kommer nu att granska ditt utdrag och återkommer ifall vi har frågor.
                          </p>
                          <p className="mb-4">
                            Spara ditt registerutdrag till dess att du fått din första löneutbetalning.
                          </p>
                          <p className="mb-4">
                            <a className="text-menuGreen" href={process.env.REACT_APP_FRONT_OFFICE_URL}>
                              Tillbaka till mobilsystemet
                            </a>
                          </p>
                          <p className="font-bold">Glömde du ladda upp en sida?</p>
                          <p className="mb-4">
                            Komplettera din uppladdning genom att{' '}
                            <span className="text-menuGreen cursor-pointer" onClick={() => setOpen((prev) => !prev)}>
                              välja ytterligare filer
                            </span>
                            .
                          </p>
                        </>
                      ) : (
                        <ol className="list-decimal list-inside mb-6">
                          Ladda upp den PDF-fil du fick i din digitala brevlåda
                        </ol>
                      )}
                    </div>

                    {ongoingRegisterRequest.complete ? (
                      open && (
                        <>
                          <FileUpload
                            ref={digitalUploadRef}
                            ongoingRegisterRequest={ongoingRegisterRequest}
                            onSuccessChange={setDigitalUploadComplete}
                          />
                          <div className="text-[#8B8B8B] text-sm sm:text-lg">
                            <p className="font-bold">Säker filuppladdning</p>
                            <p className="mb-4">
                              Uppladdade filer krypteras och lagras säkert. Utdragen raderas automatiskt efter hantering
                              i enlighet med GDPR.
                            </p>
                          </div>
                        </>
                      )
                    ) : (
                      <>
                        <FileUpload
                          ref={digitalUploadRef}
                          ongoingRegisterRequest={ongoingRegisterRequest}
                          onSuccessChange={setDigitalUploadComplete}
                        />

                        <div className="text-[#8B8B8B] text-sm sm:text-lg">
                          <p className="font-bold">Säker filuppladdning</p>
                          <p className="mb-4">
                            Uppladdade filer krypteras och lagras säkert. Registerutdrag raderas automatiskt efter
                            hantering i enlighet med GDPR.
                          </p>

                          <p className="font-bold mb-3">För dig som saknar registerutdrag:</p>

                          <ol className="list-decimal list-inside">
                            <li className="pl-2 mb-2">
                              Beställ ett nytt registerutdrag från{' '}
                              <a
                                className="text-menuGreen"
                                href="https://polisen.se/tjanster-tillstand/belastningsregistret/arbete-i-hemmet-med-aldre-och-personer-med-funktionsnedsattning-e-tjanst/"
                                rel="noreferrer"
                                target="_blank"
                              >
                                denna länk
                              </a>
                            </li>
                            <li className="pl-2 mb-2">Vänta ca 2 veckor innan registerutdraget anländer med posten.</li>
                            <li className="pl-2 mb-2">Återgå till denna sida för att ladda upp registerutdraget</li>
                          </ol>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-md sm:text-2xl text-center mb-6">
                      <span className="font-bold">
                        Registerutdrag i pappersform {ongoingRegisterRequest.complete && 'uppladdat!'}
                      </span>
                      {!ongoingRegisterRequest.complete && '(uppladdning)'}
                    </p>

                    <div className="text-sm sm:text-lg">
                      {ongoingRegisterRequest.complete ? (
                        <>
                          <p className="mb-4">
                            Vi kommer nu att granska ditt utdrag och återkommer ifall vi har frågor.
                          </p>
                          <p className="mb-4">
                            Spara ditt registerutdrag till dess att du fått din första löneutbetalning.
                          </p>
                          <p className="mb-4">
                            <a className="text-menuGreen" href={process.env.REACT_APP_FRONT_OFFICE_URL}>
                              Tillbaka till mobilsystemet
                            </a>
                          </p>
                          <p className="font-bold">Glömde du ladda upp en sida?</p>
                          <p className="mb-4">
                            Komplettera din uppladdning genom att{' '}
                            <span className="text-menuGreen cursor-pointer" onClick={() => setOpen((prev) => !prev)}>
                              välja ytterligare filer
                            </span>
                            .
                          </p>
                        </>
                      ) : (
                        <ol className="list-decimal list-inside mb-6">
                          <li className="pl-2 mb-2">
                            Fotografera eller scanna-in ditt utdrag ur:
                            <ol className="ml-4 list-[lower-alpha] pl-5 mt-2">
                              <li className="pl-2 mb-2">Belastningsregistret</li>
                              <li className="pl-2 mb-2">Misstankeregistret</li>
                            </ol>
                          </li>
                          <li className="pl-2 mb-2">
                            För varje inskannad sida, kontrollera att all information på sidan är läsbar.
                          </li>
                          <li className="pl-2">Ladda upp ditt registerutdrag som bilder eller som en PDF.</li>
                        </ol>
                      )}
                    </div>

                    <div className="mb-6">
                      <h3 className="font-bold text-sm sm:text-lg mb-2">Exempelutdrag</h3>

                      <button
                        type="button"
                        onClick={() => setExampleImageOpen(true)}
                        className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-menuGreen"
                        aria-label="Öppna exempelutdrag i större vy"
                      >
                        <img
                          src={exampleRegisterUpload}
                          alt="Exempel på rätt uppladdning av belastningsregistret och misstankeregistret"
                          className="h-auto w-full object-contain"
                        />
                      </button>

                      <p className="mt-2 text-xs sm:text-sm text-[#8B8B8B]">Klicka på bilden för att förstora.</p>
                    </div>

                    {ongoingRegisterRequest.complete ? (
                      open && (
                        <>
                          <FileUpload ref={digitalUploadRef} ongoingRegisterRequest={ongoingRegisterRequest} />
                          <div className="text-[#8B8B8B] text-sm sm:text-lg">
                            <p className="font-bold">Säker filuppladdning</p>
                            <p className="mb-4">
                              Uppladdade filer krypteras och lagras säkert. Utdragen raderas automatiskt efter hantering
                              i enlighet med GDPR.
                            </p>
                          </div>
                        </>
                      )
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                          <div>
                            <h3 className="font-bold text-sm sm:text-lg mb-2">Belastningsregistret</h3>
                            <FileUpload
                              ref={criminalRecordUploadRef}
                              ongoingRegisterRequest={ongoingRegisterRequest}
                              onSuccessChange={setCriminalRecordUploadComplete}
                            />
                          </div>

                          <div>
                            <h3 className="font-bold text-sm sm:text-lg mb-2">Misstankeregistret</h3>
                            <FileUpload
                              ref={suspicionRecordUploadRef}
                              ongoingRegisterRequest={ongoingRegisterRequest}
                              onSuccessChange={setSuspicionRecordUploadComplete}
                            />
                          </div>
                        </div>

                        <div className="text-[#8B8B8B] text-sm sm:text-lg">
                          <p className="font-bold">Säker filuppladdning</p>
                          <p className="mb-4">
                            Uppladdade filer krypteras och lagras säkert. Registerutdrag raderas automatiskt efter
                            hantering i enlighet med GDPR.
                          </p>

                          <p className="font-bold mb-3">För dig som saknar registerutdrag:</p>

                          <ol className="list-decimal list-inside">
                            <li className="pl-2 mb-2">
                              Beställ ett nytt registerutdrag från{' '}
                              <a
                                className="text-menuGreen"
                                href="https://polisen.se/tjanster-tillstand/belastningsregistret/arbete-i-hemmet-med-aldre-och-personer-med-funktionsnedsattning-e-tjanst/"
                                rel="noreferrer"
                                target="_blank"
                              >
                                denna länk
                              </a>
                            </li>
                            <li className="pl-2 mb-2">Vänta ca 2 veckor innan registerutdraget anländer med posten.</li>
                            <li className="pl-2 mb-2">Återgå till denna sida för att ladda upp registerutdraget</li>
                          </ol>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-md sm:text-2xl text-center mb-6">
                <span className="font-bold">Registerutdrag</span> (uppladdning)
              </p>
              <p className="text-[#8B8B8B] text-sm sm:text-lg text-center">
                För närvarande finns ingen begäran för dig att lämna in registerutdrag.
              </p>
            </>
          )}
        </div>
      </div>

      {canContinue && (
        <div className="fixed inset-x-0 bottom-5 z-50 flex justify-center pb-[env(safe-area-inset-bottom)] pointer-events-none">
          <Button
            variant="contained"
            onClick={handleContinue}
            endIcon={<SendIcon fontSize="small" />}
            className="pointer-events-auto !rounded-xl !px-10 !py-3 !text-base !font-semibold !tracking-wider hover:bg-emerald-50"
            sx={{
              textTransform: 'uppercase',
              backgroundColor: '#439676',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            FORTSÄTT
          </Button>
        </div>
      )}

      <Dialog
        open={exampleImageOpen}
        onClose={() => setExampleImageOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.88)',
          },
        }}
      >
        <IconButton
          aria-label="Stäng exempelutdrag"
          onClick={() => setExampleImageOpen(false)}
          sx={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 1,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            minHeight: '100dvh',
            p: { xs: 2, sm: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={exampleRegisterUpload}
            alt="Exempel på rätt uppladdning av belastningsregistret och misstankeregistret"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 12,
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmRegisterTypeSwitchDialog
        open={confirmSwitchOpen}
        loading={switchingRegisterType}
        onCancel={cancelRegisterTypeSwitch}
        onConfirm={confirmRegisterTypeSwitch}
      />
    </main>
  );
}
